import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Loader2, MailCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await forgotPassword(email.trim());
      setMessage(data?.message || "Se l'email esiste, riceverai un link.");
      setSent(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err.message ||
          "Errore."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-20 max-w-xl mx-auto" data-testid="forgot-password-page">
      <PageHeader
        eyebrow="Recupero password"
        title="Password dimenticata?"
        description="Inserisci la tua email: ti invieremo un link per reimpostare la password."
        testId="forgot-header"
      />
      <div className="glass-card p-6 sm:p-8">
        {sent ? (
          <div className="text-center py-4" data-testid="forgot-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center mx-auto mb-6">
              <MailCheck className="w-7 h-7 text-emerald-300" />
            </div>
            <p className="text-sm text-white/70 font-light leading-relaxed mb-8">
              {message}
            </p>
            <Link
              to="/auth"
              data-testid="forgot-back-to-login"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all"
            >
              Torna al login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
            <label className="block">
              <span className="block text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
                Email
              </span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  data-testid="forgot-input-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
                  placeholder="tu@esempio.com"
                />
              </div>
            </label>
            {error && (
              <div
                data-testid="forgot-error"
                className="text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-400/90 text-black text-[12px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Invia link di recupero
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <div className="text-center pt-2">
              <Link
                to="/auth"
                data-testid="forgot-back-link"
                className="text-[11px] tracking-[0.22em] uppercase text-white/40 hover:text-white/70 transition-colors"
              >
                Torna al login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
